import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { CoursesPageSettingsCard } from "@/features/admin/components/CoursesPageSettingsCard";
import { Table, TableCard } from "@/components/application/table/table";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { ButtonGroup, ButtonGroupItem } from "@/components/base/button-group/button-group";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { Input } from "@/components/base/input/input";
import {
  getCourseAudienceLabel,
  getCourseLevelLabel,
  getCourseStatusSummary,
  toNum,
} from "@/utils/course-formatters";
import { formatDurationCompact } from "@/components/base/slider/course-duration-slider";
import { adminRestoreCourse, useAdminListCourses } from "@/services/admin/courses";
import type {
  AdminCourseListItemDto,
  PagedResultOfAdminCourseListItemDto,
} from "@/features/admin/types";
import { CourseAudience, CourseLevel } from "@/features/admin/types";
import { Archive, BookOpen01, LayersThree01, Plus, Rocket01, SearchSm } from "@untitledui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { usePaymentsEnabled } from "@/hooks/useFeatureFlags";
import { isAxiosError } from "axios";
import type { CoursesFlashState } from "./-flashState";

export const Route = createFileRoute("/admin/courses/")({
  component: CoursesPage,
});

type FlashMessage = { type: "success" | "error"; message: string } | null;

const pageSize = 10;

type StatusFilter = "all" | "published" | "draft" | "archived";

function CoursesPage() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [flash, setFlash] = useState<FlashMessage>(null);
  const navigate = useNavigate();
  const routerState = useRouterState();
  const queryClient = useQueryClient();
  const paymentsEnabled = usePaymentsEnabled();
  const [restorePendingId, setRestorePendingId] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      Search: searchQuery.trim() === "" ? undefined : searchQuery.trim(),
      Page: page,
      PageSize: pageSize,
      IncludeArchived: statusFilter === "archived",
    }),
    [searchQuery, page, statusFilter],
  );

  const { data, isLoading, isFetching, error } = useAdminListCourses(params, {
    placeholderData: (previous: PagedResultOfAdminCourseListItemDto | undefined) => previous,
    staleTime: 30_000,
  });

  const errorMessage = error instanceof Error ? error.message : undefined;

  useEffect(() => {
    const incomingFlash = (routerState.location.state as unknown as CoursesFlashState | undefined)
      ?.flash;
    if (incomingFlash) {
      setFlash(incomingFlash);
      setTimeout(() => setFlash(null), 4000);
      navigate({ to: "/admin/courses", replace: true, state: undefined, search: (prev) => prev });
    }
  }, [navigate, routerState.location.state]);

  const rawItems: AdminCourseListItemDto[] = useMemo(() => data?.items ?? [], [data?.items]);

  const showNoCoursesYet =
    !errorMessage &&
    !isLoading &&
    !isFetching &&
    (data?.totalCount ?? 0) === 0 &&
    statusFilter === "all" &&
    searchQuery.trim() === "";

  const filteredItems = useMemo(() => {
    switch (statusFilter) {
      case "published":
        return rawItems.filter((course) => course.isPublished && !course.isArchived);
      case "draft":
        return rawItems.filter((course) => !course.isPublished && !course.isArchived);
      case "archived":
        return rawItems.filter((course) => course.isArchived);
      default:
        return rawItems.filter((course) => !course.isArchived);
    }
  }, [rawItems, statusFilter]);

  const showPagination = statusFilter === "all";
  const totalPages = useMemo(() => {
    if (!showPagination) return 1;
    if (!data?.totalCount) return 1;
    return Math.max(1, Math.ceil(toNum(data.totalCount) / (toNum(data.pageSize) || pageSize)));
  }, [data, showPagination]);

  const summaryMetrics = useMemo(() => {
    const totalCourses = data?.totalCount ?? 0;
    const published = rawItems.filter((course) => course.isPublished && !course.isArchived).length;
    const drafts = rawItems.filter((course) => !course.isPublished && !course.isArchived).length;
    const archived = rawItems.filter((course) => course.isArchived).length;

    return [
      {
        key: "total",
        label: "Total courses",
        value: totalCourses,
        description: totalCourses === 1 ? "Course in the catalogue" : "Courses in the catalogue",
        icon: BookOpen01,
      },
      {
        key: "published",
        label: "Published",
        value: published,
        description: published === 1 ? "Course ready for learners" : "Courses ready for learners",
        icon: Rocket01,
      },
      {
        key: "draft",
        label: "Drafts",
        value: drafts,
        description: drafts === 1 ? "Draft awaiting publish" : "Drafts awaiting publish",
        icon: LayersThree01,
      },
      {
        key: "archived",
        label: "Retired",
        value: archived,
        description: archived === 1 ? "Course retired" : "Courses retired",
        icon: Archive,
      },
    ];
  }, [data?.totalCount, rawItems]);

  const handleSearchSubmit = () => {
    setPage(1);
    setSearchQuery(searchInput.trim());
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
  };

  const renderFlashMessage = () =>
    flash && (
      <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 w-full max-w-md -translate-x-1/2 px-4">
        <div
          className={
            flash.type === "success"
              ? "pointer-events-auto rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-700 shadow-lg"
              : "pointer-events-auto rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700 shadow-lg"
          }
          role="status"
          aria-live="polite"
        >
          {flash.message}
        </div>
      </div>
    );

  const formatDate = (value: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
    } catch (error) {
      console.error("Failed to format date", error);
      return value;
    }
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const handleRestoreCourse = async (courseId: string, title: string) => {
    try {
      setRestorePendingId(courseId);
      await adminRestoreCourse(courseId);
      await queryClient.invalidateQueries({ queryKey: ["admin", "courses"] });
      setFlash({ type: "success", message: `Restored ${title}` });
      setTimeout(() => setFlash(null), 4000);
    } catch (err) {
      const message =
        isAxiosError(err) && typeof err.response?.data?.message === "string"
          ? err.response.data.message
          : err instanceof Error
            ? err.message
            : "Unable to restore course";
      setFlash({ type: "error", message });
      setTimeout(() => setFlash(null), 4000);
    } finally {
      setRestorePendingId(null);
    }
  };

  const statusFilterItems: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "All courses" },
    { key: "published", label: "Published" },
    { key: "draft", label: "Drafts" },
    { key: "archived", label: "Retired" },
  ];

  return (
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <div className="bg-secondary_alt pt-10 pb-16">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 sm:px-6 lg:px-8">
            <div className="border-secondary bg-primary flex flex-col gap-6 rounded-2xl border p-6 shadow-xs md:flex-row md:items-center md:justify-between">
              <div className="space-y-2">
                <p className="text-tertiary text-xs font-semibold tracking-[0.08em] uppercase">
                  Admin
                </p>
                <h1 className="text-primary text-3xl font-semibold">Courses</h1>
                <p className="text-tertiary max-w-xl text-sm">
                  Review catalogue health, monitor publishing progress and create new training
                  resources for the team.
                </p>
              </div>
              <div className="flex items-center gap-3 self-start md:self-center">
                <Button
                  color="primary"
                  size="md"
                  iconLeading={Plus}
                  onClick={() => navigate({ to: "/admin/courses/new" })}
                >
                  Add new course
                </Button>
              </div>
            </div>

            <CoursesPageSettingsCard />

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {summaryMetrics.map((metric) => {
                const Icon = metric.icon;
                return (
                  <div
                    key={metric.key}
                    className="border-secondary bg-primary ring-secondary flex flex-col justify-between gap-4 rounded-2xl border px-5 py-4 shadow-xs ring-1 ring-inset"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-tertiary text-xs font-semibold tracking-[0.08em] uppercase">
                          {metric.label}
                        </p>
                        <p className="text-primary mt-3 text-3xl font-semibold">
                          {metric.value.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-secondary_alt text-brand-secondary flex size-10 items-center justify-center rounded-lg">
                        <Icon className="size-5" />
                      </div>
                    </div>
                    <p className="text-tertiary text-sm">{metric.description}</p>
                  </div>
                );
              })}
            </div>

            <TableCard.Root className="border-secondary bg-primary overflow-hidden rounded-2xl border shadow-md">
              <TableCard.Header
                title="Course catalogue"
                description="Search, filter and manage the training content available to learners."
                contentTrailing={
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                    <div className="flex w-full items-center gap-2 sm:w-72">
                      <Input
                        value={searchInput}
                        onChange={(value) => setSearchInput(value || "")}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search courses"
                        icon={SearchSm}
                        size="md"
                      />
                      <Button color="secondary" size="md" onClick={handleSearchSubmit}>
                        Search
                      </Button>
                    </div>
                  </div>
                }
              />

              <div className="border-secondary flex flex-col gap-4 border-b px-4 py-4 md:px-6 md:py-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <ButtonGroup
                    selectedKeys={new Set([statusFilter])}
                    onSelectionChange={(keys) => {
                      const [next] = Array.from(keys) as StatusFilter[];
                      if (next) {
                        setStatusFilter(next);
                      }
                    }}
                    size="md"
                  >
                    {statusFilterItems.map((item) => (
                      <ButtonGroupItem key={item.key} id={item.key}>
                        {item.label}
                      </ButtonGroupItem>
                    ))}
                  </ButtonGroup>
                  <p className="text-tertiary text-sm">
                    Showing
                    <span className="text-primary ml-1 font-semibold">{filteredItems.length}</span>
                    <span className="ml-1">courses on this page</span>
                  </p>
                </div>
              </div>

              {renderFlashMessage()}

              <div className="relative min-h-[360px]">
                {(isLoading || isFetching) && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
                    <div className="border-t-arc-base-black h-8 w-8 animate-spin rounded-full border-2 border-gray-300" />
                  </div>
                )}

                {errorMessage && (
                  <div className="m-4 rounded border border-red-300 bg-red-50 p-3 text-red-800">
                    {errorMessage ?? "Failed to load courses"}
                  </div>
                )}

                {showNoCoursesYet ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    There are no courses yet. Create your first course to get started.
                  </div>
                ) : null}

                {!showNoCoursesYet &&
                !errorMessage &&
                filteredItems.length === 0 &&
                !isLoading &&
                !isFetching ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No courses found. Try adjusting your search or filter selections.
                  </div>
                ) : null}

                {filteredItems.length > 0 && (
                  <Table aria-label="Courses table">
                    <Table.Header columns={[{}, {}, {}, {}, {}, {}] as object[]}>
                      <Table.Head label="Course" isRowHeader />
                      <Table.Head label="Audience & level" />
                      <Table.Head label="Versions" />
                      <Table.Head label="Length" />
                      <Table.Head label="Status" />
                      <Table.Head label="" className="text-right" />
                    </Table.Header>
                    <Table.Body>
                      {filteredItems.map((course) => {
                        if (!course.id) {
                          return null;
                        }
                        const status = getCourseStatusSummary(course);
                        const courseId = course.id;
                        const courseTitle = course.title ?? "Untitled course";
                        const courseDescription = course.shortDescription ?? "";
                        const audience = (course.audience ?? CourseAudience.None) as CourseAudience;
                        const level = (course.level ?? CourseLevel.Unknown) as CourseLevel;
                        const updatedAt = course.updatedAt ?? "";
                        return (
                          <Table.Row key={courseId} columns={[{}, {}, {}, {}, {}, {}] as object[]}>
                            <Table.Cell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-900 md:text-base">
                                    {courseTitle}
                                  </span>
                                  {course.isFeatured && (
                                    <Badge color="brand" size="sm">
                                      Featured
                                    </Badge>
                                  )}
                                  {paymentsEnabled && course.isPaid && (
                                    <Badge color="success" size="sm">
                                      {course.priceAmountPence != null
                                        ? `Paid · £${(toNum(course.priceAmountPence) / 100).toFixed(2)}`
                                        : "Paid"}
                                    </Badge>
                                  )}
                                </div>
                                <p className="line-clamp-2 text-xs text-gray-600 md:text-sm">
                                  {courseDescription}
                                </p>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="flex flex-col text-sm text-gray-700">
                                <span>{getCourseAudienceLabel(audience)}</span>
                                <span className="text-xs text-gray-500 md:text-sm">
                                  {getCourseLevelLabel(level)}
                                </span>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="text-sm text-gray-700">
                                <span className="font-semibold text-gray-900">
                                  {course.publishedVersionCount ?? 0}
                                </span>
                                <span className="text-gray-500">
                                  {" "}
                                  of {course.versionCount ?? 0} published
                                </span>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="text-sm text-gray-700">
                                {course.courseLengthMins != null
                                  ? formatDurationCompact(toNum(course.courseLengthMins))
                                  : "—"}
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="flex flex-col gap-1 text-sm">
                                <Badge color={status.tone} size="sm">
                                  {status.label}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  Updated {formatDate(updatedAt)}
                                </span>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="flex justify-end gap-2">
                                {course.isArchived && (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={() => handleRestoreCourse(courseId, courseTitle)}
                                    isLoading={restorePendingId === courseId}
                                  >
                                    Restore
                                  </Button>
                                )}
                                <Button
                                  color="secondary"
                                  size="sm"
                                  onClick={() => navigate({ to: `/admin/courses/${courseId}` })}
                                >
                                  Edit
                                </Button>
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                )}
              </div>

              {showPagination && filteredItems.length > 0 && totalPages > 1 && (
                <PaginationPageMinimalCenter
                  page={page}
                  total={totalPages}
                  onPageChange={(nextPage) => {
                    if (nextPage < 1 || nextPage > totalPages) return;
                    setPage(nextPage);
                  }}
                />
              )}
            </TableCard.Root>
          </div>
        </div>
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
